/**
 * Builds the multi-sheet Excel/Word/PDF export for the Civil & Criminal
 * Offences workspace (MOR-738).
 *
 * One workbook, sheets are views + heads:
 *   - "Heads Summary" — one row per offence head, with the agent outcome and
 *     the practitioner's decision roll-up.
 *   - "All Findings"  — every active finding across the selected heads, with
 *     what it inherits from and its source documents.
 *   - "Transactions"  — the individual flagged transactions each finding
 *     inherits (deduped the way the review modal's Transactions tab is).
 *   - "Evidence"      — every evidence citation, with the referenced
 *     transaction snippet and the source document.
 *   - one sheet per head — that head's findings.
 *
 * Money is written as real numbers and dates as real Date cells so the
 * practitioner can sort / sum / filter natively in Excel. A head selection
 * (empty = all) scopes which per-head sheets are emitted and which rows feed
 * the other sheets.
 */

import type { ExportContent, ExportSheet } from "@/utils/export";
import { parseDateCell } from "@/components/jobs/phases/gather/summarization/bankTransactions/buildBankTransactionsExport";
import {
  agentText,
  fmtCapacity,
  friendlySource,
  humaniseTokens,
  offenceOutcomeDisplay,
  titleCase,
  BANDING_BADGE,
  OFFENCE_DECISION_BADGE,
  type OffenceFindingUI,
  type OffenceHeadKey,
  type OffenceHeadUI,
  type OffenceWorkspaceUI,
} from "./offenceShared";

/** Row shape returned by publicQueries.listFindingsForExport. */
export interface OffenceFindingExportRow extends Omit<
  OffenceFindingUI,
  "evidence"
> {
  evidence: Array<{
    note: string;
    detail: string | null;
    source: string | null;
    documentFileName: string | null;
  }>;
  transactions: Array<{
    counterparty: string;
    relationship: string | null;
    date: string | null;
    amount: number;
    quantum: number | null;
    flaggedHeadKey: string | null;
    documents: string[];
  }>;
  sourceDocuments: string[];
  inherited: Array<{
    table: string;
    flaggedHeadKey: string | null;
    note: string | null;
  }>;
}

const FINDING_HEADERS = [
  "Head",
  "Provision",
  "Respondent",
  "Capacity",
  "Limb",
  "Summary",
  "Conduct",
  "Conduct Period",
  "Standard & Shortfall",
  "Knowledge Basis",
  "Quantum",
  "Banding",
  "Risk",
  "Criminal Overlay",
  "Defence Indicators",
  "Decision",
  "Manual Finding",
  "Inherited From",
  "Source Documents",
];

const prose = (s?: string) => (s ? agentText(humaniseTokens(s)) : "");

function findingRows(
  findings: OffenceFindingExportRow[],
  headByKey: Map<OffenceHeadKey, OffenceHeadUI>,
): ExportSheet["rows"] {
  return findings.map((f) => {
    const head = headByKey.get(f.headKey);
    const overlay = f.criminalOverlay?.engaged
      ? [f.criminalOverlay.provision, f.criminalOverlay.faultIndicator]
          .filter(Boolean)
          .join(" — ")
      : "";
    return [
      head?.name ?? friendlySource(f.headKey),
      f.provision,
      f.respondentName,
      fmtCapacity(f.respondentCapacity),
      f.limb ?? "",
      prose(f.summary),
      prose(f.conductSummary),
      f.conductPeriod ?? "",
      prose(f.standardAndShortfall),
      prose(f.knowledgeBasis),
      f.quantum ?? "",
      BANDING_BADGE[f.banding]?.label ?? titleCase(f.banding),
      f.risk ? titleCase(f.risk) : "",
      overlay,
      (f.defenceIndicators ?? []).join("; "),
      OFFENCE_DECISION_BADGE[f.decision]?.label ?? titleCase(f.decision),
      f.isManualFinding ? "Yes" : "No",
      f.inherited
        .map((r) => friendlySource(r.flaggedHeadKey ?? r.table))
        .join("; "),
      f.sourceDocuments.join("; "),
    ];
  });
}

export function buildOffencesExport(
  workspace: OffenceWorkspaceUI,
  findings: OffenceFindingExportRow[],
  selectedHeadKeys: OffenceHeadKey[],
): ExportContent {
  const headSet =
    selectedHeadKeys.length > 0 ? new Set(selectedHeadKeys) : null;
  const heads = workspace.heads.filter(
    (h) => !headSet || headSet.has(h.headKey),
  );
  const headByKey = new Map(workspace.heads.map((h) => [h.headKey, h]));
  const headName = (f: OffenceFindingExportRow) =>
    headByKey.get(f.headKey)?.name ?? friendlySource(f.headKey);

  // Head order across findings, largest quantum first within each head.
  const headIdx = new Map(workspace.heads.map((h, i) => [h.headKey, i]));
  const scoped = findings
    .filter((f) => !headSet || headSet.has(f.headKey))
    .sort(
      (a, b) =>
        (headIdx.get(a.headKey) ?? 99) - (headIdx.get(b.headKey) ?? 99) ||
        (b.quantum ?? 0) - (a.quantum ?? 0),
    );

  const summarySheet: ExportSheet = {
    name: "Heads Summary",
    headers: [
      "Head",
      "Provision",
      "Outcome",
      "Findings",
      "Total Value",
      "Referrals",
      "Pending",
      "Actioned",
      "Dismissed",
      "Window From",
      "Window To",
      "Headline",
    ],
    rows: heads.map((h) => [
      h.name,
      h.provision,
      offenceOutcomeDisplay(h).label,
      h.findingCount,
      h.totalValue,
      h.referralCount,
      h.decisions.pending,
      h.decisions.actioned,
      h.decisions.dismissed,
      parseDateCell(h.reviewWindow.start),
      parseDateCell(h.reviewWindow.end),
      h.headline ? agentText(h.headline) : "",
    ]),
  };

  const allSheet: ExportSheet = {
    name: "All Findings",
    headers: FINDING_HEADERS,
    rows: findingRows(scoped, headByKey),
  };

  // The individual flagged transactions each finding inherits.
  const txnSheet: ExportSheet = {
    name: "Transactions",
    headers: [
      "Head",
      "Respondent",
      "Counterparty",
      "Relationship",
      "Date",
      "Amount",
      "Recoverable",
      "Flagged Under",
      "Source Documents",
    ],
    rows: scoped.flatMap((f) =>
      f.transactions.map((t) => [
        headName(f),
        f.respondentName,
        t.counterparty,
        t.relationship ? titleCase(t.relationship) : "",
        parseDateCell(t.date ?? undefined),
        t.amount,
        t.quantum ?? "",
        t.flaggedHeadKey ? friendlySource(t.flaggedHeadKey) : "",
        t.documents.join("; "),
      ]),
    ),
  };

  const evidenceSheet: ExportSheet = {
    name: "Evidence",
    headers: [
      "Head",
      "Respondent",
      "Evidence",
      "Transaction Detail",
      "Source",
      "Source Document",
    ],
    rows: scoped.flatMap((f) =>
      f.evidence.map((e) => [
        headName(f),
        f.respondentName,
        agentText(e.note),
        e.detail ?? "",
        e.source ? friendlySource(e.source) : "",
        e.documentFileName ?? "",
      ]),
    ),
  };

  // One sheet per head that has findings.
  const headSheets: ExportSheet[] = heads
    .map((h) => ({
      head: h,
      findings: scoped.filter((f) => f.headKey === h.headKey),
    }))
    .filter((entry) => entry.findings.length > 0)
    .map((entry) => ({
      name: entry.head.shortLabel,
      headers: FINDING_HEADERS,
      rows: findingRows(entry.findings, headByKey),
    }));

  return {
    title: "Civil & Criminal Offences",
    sheets: [summarySheet, allSheet, txnSheet, evidenceSheet, ...headSheets],
  };
}
